import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** the display's Close and Reveal (RoundDisplayUi.cpp kCloseMs, kRevealMs) */
const CLOSE_MS = 800;
const REVEAL_MS = 900;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/**
 * The iris of the round display around the ring (Dominik 22.10.2026: the animations the same as on the display):
 * going into standby it closes over the ring that was shown, with a crema rim, down to a glowing dot that fades out;
 * coming out of it, it opens onto the new ring while the values count up from zero (`children` gets the reveal's
 * progress, 0..1 eased, null otherwise). The circle is CSS (clip-path on a registered custom property, Iris in
 * components.css), the count-up one frame at a time. The first render shows the ring without a motion.
 */
export function Iris({
  closed,
  children,
}: {
  closed: boolean;
  children: (reveal: number | null) => ReactNode;
}) {
  const [phase, setPhase] = useState<'none' | 'closing' | 'revealing'>('none');
  const [reveal, setReveal] = useState<number | null>(null);
  const was = useRef(closed);
  // the last ring while awake: what the iris closes over (its props as they were then)
  const awake = useRef<ReactNode>(null);

  // set before the paint, so the new state never shows a frame without its motion
  useLayoutEffect(() => {
    if (was.current === closed) return;
    was.current = closed;
    setPhase(closed ? 'closing' : 'revealing');
    if (!closed) setReveal(0);
  }, [closed]);

  useEffect(() => {
    if (phase === 'none') return;
    if (phase === 'closing') {
      const timer = window.setTimeout(() => setPhase('none'), CLOSE_MS);
      return () => window.clearTimeout(timer);
    }
    const start = performance.now();
    let raf = requestAnimationFrame(function frame(now) {
      const t = Math.min(1, (now - start) / REVEAL_MS);
      setReveal(easeOutCubic(t));
      if (t < 1) raf = requestAnimationFrame(frame);
      else {
        setReveal(null);
        setPhase('none');
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  if (phase === 'closing')
    return (
      <div className="iris is-closing" aria-hidden="true">
        <div className="iris-scene">{awake.current}</div>
        <span className="iris-rim" />
        <span className="iris-dot" />
      </div>
    );
  const node = children(phase === 'revealing' ? (reveal ?? 0) : null);
  if (!closed && phase === 'none') awake.current = node;
  if (phase === 'revealing')
    return (
      <div className="iris is-revealing">
        <div className="iris-scene">{node}</div>
        <span className="iris-rim" />
      </div>
    );
  return <>{node}</>;
}
